/**
 * "Alerting is not working" - said on the dashboard, because it cannot be said
 * by email.
 *
 * This is the whole feature. If Resend is failing, every other channel we have
 * is failing with it, so the only place the news can reliably arrive is the
 * screen somebody is already looking at.
 *
 * NOT DISMISSIBLE, deliberately, unlike the brief. A dismissible warning about
 * silent alerting would be dismissed once and then be exactly as useful as no
 * warning at all - and the condition it reports can persist for days without
 * producing any other symptom. It disappears when the next message sends, and
 * not before.
 *
 * Shown only to people who can do something about it (alerts.manage). A
 * technician who cannot reach the settings gains nothing from being told, and
 * a banner that most of the team learns to scroll past is a banner nobody
 * reads on the day it matters.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Health {
  broken: boolean;
  message: string | null;
}

export function AlertHealthBanner({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [h, setH] = useState<Health | null>(null);

  useEffect(() => {
    if (!orgId || !canManage) return;
    let alive = true;

    const check = async () => {
      try {
        const r = await httpsCallable(functions, 'getAlertHealth')({ orgId });
        if (alive) setH(r.data as Health);
      } catch {
        // A failed check must not itself raise the alarm. Showing "alerting is
        // broken" because one callable timed out would teach people that the
        // banner means nothing.
      }
    };

    void check();
    // Every five minutes. Alerting either works or it does not; polling harder
    // buys nothing and this sits on the dashboard all day.
    const id = setInterval(() => void check(), 5 * 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [orgId, canManage]);

  if (!h?.broken) return null;

  return (
    <div className="banner banner-error" style={{ marginBottom: 12 }}>
      <strong>Alerting is not reaching anyone.</strong>{' '}
      {h.message}
      <div style={{ marginTop: 6, fontSize: 12 }}>
        Backups are still being checked and this board is still correct &mdash; only the emails are
        failing. Check your Resend key and sending domain under Integrations.
      </div>
    </div>
  );
}
